import React, { useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isValidDob, DATE_MIN, todayISO } from '../../utils/validators';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  CloudUpload,
  Visibility,
  VisibilityOff,
  CheckCircle,
} from '@mui/icons-material';
import axios from 'axios';
import config from '../../config';

const GRADIENT = 'linear-gradient(90deg, #724F96 0%, #C084FC 100%)';
const TITLE_COLOR = '#724F96';

const salutations = ['Ms.', 'Mr.', 'Mrs.', 'Mx.', 'Dr.'];
const countryCodes = ['+91', '+1', '+44', '+971'];

function UploadSlot({ label, hint, file, onFile, inputRef, disabled, error }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ color: TITLE_COLOR, fontWeight: 700, fontSize: 14, mb: 0.5 }}>{label}</Typography>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg"
        // NOTE: do NOT use the `hidden` attribute (or `display: none`). On
        // iOS Safari and some Android browsers, calling `.click()` on a
        // fully-hidden file input is a silent no-op — the picker never
        // opens. Render the input but make it visually invisible.
        style={{
          position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
          overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
        }}
        disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (f.size > 1024 * 1024) {
            window.alert('File must be under 1MB (JPG, JPEG, PNG).');
            e.target.value = '';
            return;
          }
          onFile(f);
        }}
      />
      <Box
        onClick={() => !disabled && inputRef.current?.click()}
        sx={{
          border: error
            ? '2px dashed rgba(211, 47, 47, 0.55)'
            : '2px dashed rgba(114, 79, 150, 0.35)',
          borderRadius: 2,
          py: 2,
          px: 1.5,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          bgcolor: error ? 'rgba(211, 47, 47, 0.04)' : 'rgba(114, 79, 150, 0.04)',
        }}
      >
        <CloudUpload sx={{ color: TITLE_COLOR, fontSize: 36, mb: 0.5 }} />
        <Typography variant="body2" color="text.secondary">
          Click to upload {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
        {file && (
          <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 600 }}>
            {file.name}
          </Typography>
        )}
      </Box>
      {error && (
        <Typography
          color="error"
          variant="caption"
          data-testid={`upload-error-${(label || '').toLowerCase()}`}
          sx={{ mt: 0.5, display: 'block' }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Multi-step B2B signup aligned with NP-B-008 / NP-B-010 / NP-B-011 style flows.
 */
export default function B2BSignupWizard({
  profileTitle,
  sendOtpPath,
  verifyOtpPath,
  submitPath,
  loginPath,
  backPath = '/signup-as',
  isValetService = false,
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const [companyName, setCompanyName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [country, setCountry] = useState('');
  const [website, setWebsite] = useState('');
  const [licenseNo, setLicenseNo] = useState('');
  const [panNo, setPanNo] = useState('');
  const [gstNo, setGstNo] = useState('');
  const [licenseFile, setLicenseFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [gstFile, setGstFile] = useState(null);
  const [uploadErrors, setUploadErrors] = useState({});
  const [legalErrors, setLegalErrors] = useState({}); // { pan, gst, license }
  const licRef = useRef();
  const panRef = useRef();
  const gstRef = useRef();

  const [salutation, setSalutation] = useState('Ms.');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [dob, setDob] = useState('');
  const [nationality, setNationality] = useState('');
  const [designation, setDesignation] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [officePhone, setOfficePhone] = useState('');

  const [otp, setOtp] = useState(['', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const otpRefs = [useRef(), useRef(), useRef(), useRef()];

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const goBack = () => {
    if (step === 'profile') navigate(backPath);
    else if (step === 'contact') setStep('profile');
    else if (step === 'otp') setStep('contact');
    else if (step === 'password') setStep('otp');
  };

  // ─── Legal field format validators (mirrored on the server) ───
  const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
  const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
  const LICENSE_RE = /^[A-Z0-9][A-Z0-9\-/\s]{3,49}$/i;

  const checkPan = (v) => {
    const s = (v || '').trim().toUpperCase();
    if (!s) return 'PAN is required.';
    if (!PAN_RE.test(s)) return 'PAN must be in the format ABCDE1234F (5 letters, 4 digits, 1 letter).';
    return '';
  };
  const checkGst = (v) => {
    // GST is optional — only format-check when the field is non-empty.
    const s = (v || '').trim().toUpperCase();
    if (!s) return '';
    if (!GSTIN_RE.test(s)) return 'GST must be 15 characters in the format 29ABCDE1234F1Z5.';
    return '';
  };
  const checkLicense = (v) => {
    const s = (v || '').trim();
    if (!s) return 'License certificate number is required.';
    if (!LICENSE_RE.test(s)) return "License must be 4–50 chars using letters, digits, '-' or '/'.";
    return '';
  };

  const validateProfile = () => {
    if (!companyName.trim() || !addressLine1.trim() || !city.trim() || !pincode.trim() || !stateVal.trim() || !country.trim()) {
      showSnackbar('Please complete company and address fields.', 'error');
      return false;
    }
    const errs = {
      pan: checkPan(panNo),
      gst: checkGst(gstNo),
      license: checkLicense(licenseNo),
    };
    setLegalErrors(errs);
    if (errs.pan || errs.gst || errs.license) {
      showSnackbar('Please fix the highlighted fields.', 'error');
      return false;
    }
    return true;
  };

  const validateContact = () => {
    if (!firstName.trim() || !lastName.trim()) {
      showSnackbar('First and last name are required.', 'error');
      return false;
    }
    if (!gender || !dob) {
      showSnackbar('Gender and date of birth are required.', 'error');
      return false;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showSnackbar('Valid email is required.', 'error');
      return false;
    }
    if (!phone.trim()) {
      showSnackbar('Phone number is required.', 'error');
      return false;
    }
    return true;
  };

  const handleVerify = async () => {
    if (!validateContact()) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${config.API_BASE_URL}${sendOtpPath}`,
        { email: email.trim(), organization_name: companyName.trim(), company_name: companyName.trim() }
      );
      if (res.data?.debug_otp != null) {
        showSnackbar(`Dev / skipped email — use code: ${res.data.debug_otp}`, 'info');
      } else {
        showSnackbar('Verification code sent to your email.', 'success');
      }
      setOtp(['', '', '', '']);
      setOtpError('');
      setStep('otp');
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } catch (e) {
      showSnackbar(e.response?.data?.error || 'Could not send verification code', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (i, val) => {
    const d = val.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[i] = d;
    setOtp(next);
    setOtpError('');
    if (d && i < 3) otpRefs[i + 1].current?.focus();
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs[i - 1].current?.focus();
  };

  const handleConfirmOtp = async () => {
    const code = otp.join('');
    if (code.length !== 4) {
      setOtpError('Enter the 4-digit code.');
      return;
    }
    setLoading(true);
    setOtpError('');
    try {
      await axios.post(
        `${config.API_BASE_URL}${verifyOtpPath}`,
        { email: email.trim().toLowerCase(), otp: code }
      );
      setStep('password');
    } catch (e) {
      const msg = e.response?.data?.error || 'Invalid verification code';
      setOtpError(msg);
      showSnackbar(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    if (password.length < 6) {
      showSnackbar('Password must be at least 6 characters.', 'error');
      return;
    }
    if (password !== confirmPassword) {
      showSnackbar('Passwords do not match.', 'error');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('email', email.trim().toLowerCase());
      fd.append('password', password);
      fd.append('email_verification_otp', otp.join(''));
      fd.append('company_name', companyName.trim());
      fd.append('address_line1', addressLine1.trim());
      fd.append('address_line2', addressLine2.trim());
      fd.append('city', city.trim());
      fd.append('pincode', pincode.trim());
      fd.append('state', stateVal.trim());
      fd.append('country', country.trim());
      fd.append('website', website.trim());
      fd.append('license_certificate_no', licenseNo.trim());
      fd.append('pan_no', panNo.trim());
      fd.append('gst_no', gstNo.trim());
      fd.append('salutation', salutation);
      fd.append('first_name', firstName.trim());
      fd.append('middle_name', middleName.trim());
      fd.append('last_name', lastName.trim());
      fd.append('gender', gender);
      fd.append('dob', dob);
      fd.append('nationality', nationality.trim());
      fd.append('designation', designation.trim());
      fd.append('country_code', countryCode);
      fd.append('phone', phone.trim());
      fd.append('office_phone', officePhone.trim());
      if (isValetService) {
        fd.append('service_display_name', companyName.trim());
      }
      if (licenseFile) fd.append('license_file', licenseFile);
      if (panFile) fd.append('pan_file', panFile);
      if (gstFile) fd.append('gst_file', gstFile);

      await axios.post(`${config.API_BASE_URL}${submitPath}`, fd);
      setStep('success');
    } catch (e) {
      const data = e.response?.data || {};
      if (data.field_errors) {
        // Server rejected one of the legal fields — map to inline errors and return to profile step
        setLegalErrors({
          pan: data.field_errors.pan_no || '',
          gst: data.field_errors.gst_no || '',
          license: data.field_errors.license_certificate_no || '',
        });
        setStep('profile');
        showSnackbar(data.error || 'Please fix the highlighted fields.', 'error');
      } else if (data.failed_doc && ['license', 'pan', 'gst'].includes(data.failed_doc)) {
        setUploadErrors((prev) => ({ ...prev, [data.failed_doc]: data.error }));
        setStep('profile');
        showSnackbar(data.error, 'error');
      } else {
        showSnackbar(data.error || 'Registration failed', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2 },
  };

  const primaryBtnSx = {
    mt: 2,
    py: 1.5,
    borderRadius: 3,
    textTransform: 'none',
    fontWeight: 800,
    fontSize: 16,
    background: GRADIENT,
    color: '#fff',
    '&:hover': { background: GRADIENT, opacity: 0.92 },
    '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff', pb: 4 }}>
      <Box sx={{ maxWidth: 420, mx: 'auto', px: 2, pt: 2 }}>
        {step !== 'success' && (
          <IconButton onClick={goBack} sx={{ color: TITLE_COLOR, mb: 1 }} aria-label="back">
            <ArrowBack />
          </IconButton>
        )}

        {step === 'profile' && (
          <>
            <Typography sx={{ color: TITLE_COLOR, fontWeight: 800, fontSize: 22, mb: 2 }}>{profileTitle}</Typography>
            <TextField fullWidth label="Company Name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} sx={{ ...fieldSx, mb: 2 }} required />
            <Typography fontWeight={700} sx={{ mb: 1, color: '#444' }}>
              Address
            </Typography>
            <TextField fullWidth label="Address Line 1" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} sx={{ ...fieldSx, mb: 2 }} required />
            <TextField fullWidth label="Address Line 2" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} sx={{ ...fieldSx, mb: 2 }} />
            <Box display="flex" gap={1} mb={2}>
              <TextField fullWidth label="City" value={city} onChange={(e) => setCity(e.target.value)} sx={fieldSx} required />
              <TextField fullWidth label="Pin code" value={pincode} onChange={(e) => setPincode(e.target.value)} sx={fieldSx} required />
            </Box>
            <Box display="flex" gap={1} mb={2}>
              <TextField fullWidth label="State" value={stateVal} onChange={(e) => setStateVal(e.target.value)} sx={fieldSx} required />
              <TextField fullWidth label="Country" value={country} onChange={(e) => setCountry(e.target.value)} sx={fieldSx} required />
            </Box>

            <UploadSlot
              label="License"
              hint="JPG, JPEG, PNG less than 1MB"
              file={licenseFile}
              onFile={(f) => { setLicenseFile(f); setUploadErrors((p) => ({ ...p, license: undefined })); }}
              inputRef={licRef}
              disabled={loading}
              error={uploadErrors.license}
            />
            <TextField
              fullWidth
              label="License No."
              value={licenseNo}
              onChange={(e) => { setLicenseNo(e.target.value); if (legalErrors.license) setLegalErrors((p) => ({ ...p, license: '' })); }}
              onBlur={() => setLegalErrors((p) => ({ ...p, license: checkLicense(licenseNo) }))}
              error={Boolean(legalErrors.license)}
              helperText={legalErrors.license || ' '}
              sx={{ ...fieldSx, mb: 2 }}
              required
            />

            <UploadSlot
              label="PAN"
              hint="JPG, JPEG, PNG less than 1MB"
              file={panFile}
              onFile={(f) => { setPanFile(f); setUploadErrors((p) => ({ ...p, pan: undefined })); }}
              inputRef={panRef}
              disabled={loading}
              error={uploadErrors.pan}
            />
            <TextField
              fullWidth
              label="PAN No."
              value={panNo}
              onChange={(e) => { setPanNo(e.target.value.toUpperCase()); if (legalErrors.pan) setLegalErrors((p) => ({ ...p, pan: '' })); }}
              onBlur={() => setLegalErrors((p) => ({ ...p, pan: checkPan(panNo) }))}
              error={Boolean(legalErrors.pan)}
              helperText={legalErrors.pan || ' '}
              inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
              sx={{ ...fieldSx, mb: 2 }}
              required
            />

            <UploadSlot
              label="GST"
              hint="JPG, JPEG, PNG less than 1MB"
              file={gstFile}
              onFile={(f) => { setGstFile(f); setUploadErrors((p) => ({ ...p, gst: undefined })); }}
              inputRef={gstRef}
              disabled={loading}
              error={uploadErrors.gst}
            />
            <TextField
              fullWidth
              label="GST No. (optional)"
              value={gstNo}
              onChange={(e) => { setGstNo(e.target.value.toUpperCase()); if (legalErrors.gst) setLegalErrors((p) => ({ ...p, gst: '' })); }}
              onBlur={() => setLegalErrors((p) => ({ ...p, gst: checkGst(gstNo) }))}
              error={Boolean(legalErrors.gst)}
              helperText={legalErrors.gst || ' '}
              inputProps={{ maxLength: 15, style: { textTransform: 'uppercase' } }}
              sx={{ ...fieldSx, mb: 2 }}
            />

            <TextField fullWidth label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} sx={{ ...fieldSx, mb: 2 }} />

            <Button fullWidth sx={primaryBtnSx} onClick={() => validateProfile() && setStep('contact')}>
              Next
            </Button>
          </>
        )}

        {step === 'contact' && (
          <>
            <Typography sx={{ color: TITLE_COLOR, fontWeight: 800, fontSize: 22, mb: 2 }}>Contact Person</Typography>
            <Box display="flex" gap={1} mb={1}>
              <FormControl sx={{ minWidth: 88 }} size="small">
                <InputLabel>Title</InputLabel>
                <Select value={salutation} label="Title" onChange={(e) => setSalutation(e.target.value)}>
                  {salutations.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} sx={fieldSx} required />
            </Box>
            <Box display="flex" gap={1} mb={2}>
              <TextField fullWidth label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} sx={fieldSx} required />
              <TextField fullWidth label="Middle Name" value={middleName} onChange={(e) => setMiddleName(e.target.value)} sx={fieldSx} />
            </Box>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel shrink>Gender</InputLabel>
              <Select value={gender} label="Gender" displayEmpty notched onChange={(e) => setGender(e.target.value)}>
                <MenuItem value="" disabled>
                  Select gender
                </MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="other">Other</MenuItem>
                <MenuItem value="prefer_not">Prefer not to say</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="date"
              label="Date of Birth"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: DATE_MIN, max: todayISO() }}
              error={Boolean(dob) && !isValidDob(dob)}
              helperText={Boolean(dob) && !isValidDob(dob) ? 'Enter a valid date (YYYY-MM-DD), not in the future' : ''}
              sx={{ ...fieldSx, mb: 2 }}
            />
            <Box display="flex" gap={1} mb={2}>
              <TextField fullWidth label="Nationality" value={nationality} onChange={(e) => setNationality(e.target.value)} sx={fieldSx} />
              <TextField fullWidth label="Designation" value={designation} onChange={(e) => setDesignation(e.target.value)} sx={fieldSx} />
            </Box>
            <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} sx={{ ...fieldSx, mb: 2 }} required />
            <Box display="flex" gap={1} mb={2} alignItems="flex-start">
              <FormControl sx={{ minWidth: 100 }} size="small">
                <InputLabel>Code</InputLabel>
                <Select value={countryCode} label="Code" onChange={(e) => setCountryCode(e.target.value)}>
                  {countryCodes.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth label="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} sx={fieldSx} required />
            </Box>
            <TextField fullWidth label="Office Number" value={officePhone} onChange={(e) => setOfficePhone(e.target.value)} sx={{ ...fieldSx, mb: 2 }} />

            <Button fullWidth sx={primaryBtnSx} onClick={handleVerify} disabled={loading}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
            </Button>
          </>
        )}

        {step === 'otp' && (
          <>
            <Typography sx={{ color: TITLE_COLOR, fontWeight: 800, fontSize: 22, mb: 1 }}>Verification Code</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please enter the verification code sent to your email ID.
            </Typography>
            {otpError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {otpError}
              </Alert>
            ) : null}
            <Box display="flex" gap={1.5} justifyContent="center" mb={3}>
              {otp.map((d, i) => (
                <TextField
                  key={i}
                  inputRef={otpRefs[i]}
                  value={d}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  inputProps={{ maxLength: 1, style: { textAlign: 'center', fontSize: 22, fontWeight: 700 } }}
                  sx={{ width: 56, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              ))}
            </Box>
            <Button fullWidth sx={primaryBtnSx} onClick={handleConfirmOtp} disabled={loading}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirm'}
            </Button>
          </>
        )}

        {step === 'password' && (
          <>
            <Typography sx={{ color: TITLE_COLOR, fontWeight: 800, fontSize: 22, mb: 1 }}>Set a New Password</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set a strong and secure password to protect your account.
            </Typography>
            <Typography sx={{ color: TITLE_COLOR, fontSize: 13, fontWeight: 600, mb: 0.5 }}>Password</Typography>
            <TextField
              fullWidth
              type={showPw ? 'text' : 'password'}
              placeholder="Enter the new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ ...fieldSx, mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPw(!showPw)} edge="end" aria-label="toggle password">
                      {showPw ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography sx={{ color: TITLE_COLOR, fontSize: 13, fontWeight: 600, mb: 0.5 }}>Confirm Password</Typography>
            <TextField
              fullWidth
              type={showPw2 ? 'text' : 'password'}
              placeholder="Confirm the password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ ...fieldSx, mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPw2(!showPw2)} edge="end" aria-label="toggle confirm password">
                      {showPw2 ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              fullWidth
              sx={primaryBtnSx}
              onClick={handleCreateAccount}
              disabled={loading || !password || !confirmPassword}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Password'}
            </Button>
          </>
        )}

        {step === 'success' && (
          <Box textAlign="center" pt={4}>
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                background: GRADIENT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: '#fff' }} />
            </Box>
            <Typography sx={{ color: TITLE_COLOR, fontWeight: 800, fontSize: 22, mb: 1 }}>Successful</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Congratulations! Your account has been created. Click below to sign in.
            </Typography>
            <Button fullWidth sx={primaryBtnSx} onClick={() => navigate(loginPath)}>
              Sign In
            </Button>
          </Box>
        )}

        {step !== 'success' && (
          <Typography align="center" sx={{ mt: 2, mb: 2, color: 'text.secondary', fontSize: 14 }}>
            Already have an account?{' '}
            <Link to={loginPath} style={{ color: TITLE_COLOR, fontWeight: 700, textDecoration: 'none' }}>
              Sign in
            </Link>
          </Typography>
        )}
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
